import React, { useState, useRef } from 'react'
import gql from 'graphql-tag'
import { useTranslation } from 'react-i18next'
import { Form, Text, Select, Checkbox } from 'components/forms'
import DataTable from 'components/DataTableTest'
import { useMutation, useQuery } from 'components/graphql'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { apaleo } from '../../gql/fragments'
import listApaleoProperties from 'gql/query/listApaleoProperties'
import listSalesGroups from '../SalesGroups/gql/listSalesGroups'
import { Row, Col } from 'react-flexbox-grid'

const query = gql`
	query getAppConfig {
		cashAssist {
			getAppConfig {
				...apaleo
			}
		}
	}
	${apaleo}
`

const listApaleoSubAccounts = gql`
	query apaleoSubAccounts($propertyId: ID!) {
		cashAssist {
			listApaleoSubAccounts(propertyId: $propertyId) {
				id
				name
				salesGroupIds
				type
			}
		}
	}
`

const mutation = gql`
	mutation updateAppConfig($fields: AppConfigInput) {
		cashAssist {
			updateAppConfig(fields: $fields) {
				...apaleo
			}
		}
	}
	${apaleo}
`

const updateApaleoSubAccounts = gql`
	mutation updateApaleoSubAccounts($propertyId: ID!, $salesGroupsConfig: [ApaleoSubAccountSalesGroups]!) {
	  cashAssist {
			updateApaleoSalesGroupIds(
	    	propertyId: $propertyId,
	    	salesGroupsConfig: $salesGroupsConfig
	    ) {
	      id
	      name
	      salesGroupIds
	      type
	    }
	  }
	}
`

const Apaleo = props => {
	const [credentialsError, setCredentialsError] = useState(null)
	const { data } = useQuery(query)
	const [mutate] = useMutation(mutation, {
		onCompleted: () => {
			setCredentialsError(null)
		}
	})
	const [t] = useTranslation()
	const hasApaleoSetup = data?.getAppConfig?.cashRegisterGlobal?.apaleaoClientId
											&& data?.getAppConfig?.cashRegisterGlobal?.apaleaoClientSecret

	const handleSubmit = values => {
		const secretIsChanged = values?.cashRegisterGlobal.apaleaoClientSecret !== data?.getAppConfig?.cashRegisterGlobal?.apaleaoClientSecret
		if(!secretIsChanged) {
			delete values?.cashRegisterGlobal?.apaleaoClientSecret
		}
		mutate({
			variables: {
				fields: values
			}
		})
	}

	return(
		<>
			<Form smartSubmit initialValues={data?.getAppConfig} onSubmit={handleSubmit}>
				<Text
					field='cashRegisterGlobal.apaleaoClientId'
					label={t('common.fields.clientId')}
					error={credentialsError}
				/>
				<Text
					field='cashRegisterGlobal.apaleaoClientSecret'
					label={t('common.fields.clientSecret')}
					type='password'
					disablePasswordAutoComplete
				/>
			</Form>
			{hasApaleoSetup && !credentialsError &&
				<>
					<Divider visible/>
					<ApaleoSalesGroups setCredentialsError={setCredentialsError}/>
					<Divider visible/>
					<ApaleoPayMethods setCredentialsError={setCredentialsError}/>
				</>
			}
		</>
	)
}

const ApaleoSalesGroups = ({ setCredentialsError }) => {
	const [t] = useTranslation()
	const [property, setProperty] = useState(null)
	const [subAccount, setSubAccount] = useState(null)
	const [hasChanged, setHasChanged] = useState(false)
	const [mutate] = useMutation(updateApaleoSubAccounts)
	const subAccSalesGroups = (!!subAccount && property.salesGroupsConfig?.find(({ id }) => id === subAccount?.id)?.salesGroupIds) || []

	const handlePropertyChange = option => {
		setHasChanged(false)
		setProperty(option)
		setSubAccount(null)
	}

	const handleMutation = () => {
		if(hasChanged && subAccount && property?.salesGroupsConfig) {
			mutate({
				variables: {
					propertyId: property.id,
					salesGroupsConfig: property.salesGroupsConfig
				}
			})
			setHasChanged(false)
		}
	}

	const handleSubAccountChange = option => {
		setSubAccount(option)
	}

	const handleSalesGroupSelect = salesGroup => {
		if(!subAccount) {
			return
		}
		setHasChanged(true)
		setProperty(oldProperty => ({
			...oldProperty,
			salesGroupsConfig: oldProperty.salesGroupsConfig.map(config => {
				if(subAccount.id !== config.id) {
					return config
				}
				const isRemove = config.salesGroupIds.find(sgid => sgid === salesGroup.id)
				return {
					...config,
					salesGroupIds: isRemove ? config.salesGroupIds.filter(sgid => sgid !== salesGroup.id) : [...config.salesGroupIds, salesGroup.id]
				}
			})
		}))
	}

	return(
		<>
			<p>{t('options_section.strings.select_salesgroups_for_apaleo')}</p>
			<Divider/>
			<Row>
				<Col xs>
					<Select
						noForm
						value={property?.id}
						onChange={handlePropertyChange}
						label={t('options_section.fields.apaleo_pms_property')}
						remoteOptions={{ query: listApaleoProperties, fetchPolicy: 'cache-and-network' }}
						valueField='id'
						labelField='name'
						isDisabled={hasChanged}
					/>
					{property &&
						<Select
							key={property?.id}
							noForm
							value={subAccount?.id}
							onChange={handleSubAccountChange}
							label={t('options_section.fields.apaleo_pms_subaccount')}
							remoteOptions={{
								query: listApaleoSubAccounts,
								variables: { propertyId: property?.id },
								fetchPolicy: 'cache-and-network',
								onCompleted: ({ cashAssist }) => {
									if(!property.salesGroupsConfig) {
										setProperty(oldProperty => ({
											...oldProperty,
											salesGroupsConfig: cashAssist.listApaleoSubAccounts.map(({ id, name, salesGroupIds, type }) => ({
												id, name, salesGroupIds, type
											}))
										}))
									}
								},
								onError: e => setCredentialsError(e.message)
							}}
							valueField='id'
							labelField='name'
						/>
					}
					{hasChanged && (
						<>
							<Button text={t('common.buttons.save')} onClick={handleMutation}/>
							<Divider/>
							<Button text={t('common.buttons.abort')} onClick={() => handlePropertyChange(null)} status='cancelation'/>
						</>
					)}
				</Col>
				<Col xs>
					<p style={{ fontSize: '12px' }}>{t('options_section.sub_sections.sales_groups')}</p>
					<DataTable
						width={400}
						height={350}
						remoteOptions={{
							query: listSalesGroups
						}}
						onSelect={handleSalesGroupSelect}
						columns={[
							{
								Header: <span className='sort-icons'>{t('common.fields.code')}</span>,
								accessor: 'acountNum',
								width: 100
							}, {
								Header: t('common.fields.name'),
								accessor: 'name',
								width: 200
							}, {
								Cell: ({ row }) => !!subAccount && <Checkbox id={row.original?.id} readOnly noForm checked={subAccSalesGroups.includes(row.original?.id)}/>,
								accessor: 'id',
								width: 25
							}]
						}
					/>
				</Col>
			</Row>
		</>
	)
}

const listApaleoPayMethods = gql`
	query listApaleoPaymentMethods {
		cashAssist {
			listApaleoPaymentMethods {
	      name
	      creditCardIndexArr
	    }
	  }
	}
`

const listCreditOptions = gql`
	query listCreditOptions {
		cashAssist {
			getAppConfig {
				creditCards
			}
		}
	}
`

const updateApaleoPaymentMethods = gql`
mutation updateApaleoPaymentMethods($paymentMethods: [ApaleoPaymentMethodCreditCard!]!) {
  cashAssist {
    updateApaleoPaymentMethods(paymentMethods: $paymentMethods) {
      name
      creditCardIndexArr
    }
  }
}
`

const ApaleoPayMethods = ({ setCredentialsError }) => {
	const [t] = useTranslation()
	const [hasChanged, setHasChanged] = useState(false)
	const [payMethods, setPayMethods] = useState(null)
	const [selectedPayMethod, setSelectedPayMethod] = useState(null)
	const { data } = useQuery(listCreditOptions)
	const [mutate] = useMutation(updateApaleoPaymentMethods)
	const queryRef = useRef(null)

	const creditOptions = data?.getAppConfig?.creditCards
		.slice(0, 9)
		.map((name, index) => ({ name, index: index + 1 }))
		.filter(({ name }) => !!name) || []
	const selectedPayMethodData = payMethods?.find(pm => pm.name === selectedPayMethod?.name)

	const handleMutation = () => {
		if(hasChanged) {
			mutate({
				variables: {
					paymentMethods: payMethods.map(({ __typename, ...rest }) => rest)
				},
				refetchQueries: [{
					query: listApaleoPayMethods
				}]
			})
			setHasChanged(false)
		}
	}

	const handleCancelation = () => {
		setHasChanged(false)
		setSelectedPayMethod(null)
		setPayMethods(queryRef.current?.data?.cashAssist?.listApaleoPaymentMethods)
	}

	const handleCreditSelect = creditOption => {
		if(!selectedPayMethod) {
			return
		}
		setHasChanged(true)
		setPayMethods(oldPayMethods => oldPayMethods.map((payMethod) => {
			if(payMethod.name !== selectedPayMethod.name) {
				return payMethod
			}
			const isRemove = payMethod.creditCardIndexArr.includes(creditOption.index)

			return {
				...payMethod,
				creditCardIndexArr: isRemove ? payMethod.creditCardIndexArr.filter(index => index !== creditOption.index) : [...payMethod.creditCardIndexArr, creditOption.index]
			}
		}))
	}


	return(
		<>
			<p>{t('options_section.strings.select_credit_for_apaleo')}</p>
			<Divider/>
			<Row>
				<Col xs>
					<Select
						noForm
						value={selectedPayMethod?.name}
						onChange={setSelectedPayMethod}
						label={t('options_section.fields.apaleo_pms_payment')}
						remoteOptions={{
							query: listApaleoPayMethods,
							ref: queryRef,
							fetchPolicy: 'cache-and-network',
							onCompleted: ({ cashAssist }) => {
								if(!hasChanged) {
									setPayMethods(cashAssist?.listApaleoPaymentMethods)
								}
							},
							onError: e => setCredentialsError(e.message)
						}}
						valueField='name'
						labelField='name'
					/>
					{hasChanged && (
						<>
							<Button text={t('common.buttons.save')} onClick={handleMutation}/>
							<Divider/>
							<Button text={t('common.buttons.abort')} onClick={handleCancelation} status='cancelation'/>
						</>
					)}
				</Col>
				<Col xs>
					<p style={{ fontSize: '12px' }}>{t('options_section.strings.credit_cards')}</p>
					{<DataTable
						width={400}
						data={creditOptions}
						onSelect={handleCreditSelect}
						columns={[{
							Header: t('common.fields.name'),
							accessor: 'name',
							width: 265
						}, {
							Cell: ({ row }) => !!selectedPayMethod && <Checkbox id={row.original?.index} readOnly noForm checked={selectedPayMethodData?.creditCardIndexArr?.includes(row.original?.index)}/>,
							accessor: 'index',
							width: 25
						}]}
					/>}
				</Col>
			</Row>
		</>
	)
}

export default Apaleo
